/**
 * ============================================================================
 * PROTECTED FEATURE BUTTON COMPONENT
 * ============================================================================
 * Enterprise-grade button wrapper for protected features.
 * 
 * Features:
 * - Visual lock indicator when not authenticated
 * - Smooth Microsoft login popup flow
 * - Professional loading states
 * - Clear user feedback
 * 
 * Usage:
 *   <ProtectedFeatureButton
 *     featureName="Leaderboard"
 *     onAccess={() => navigate('/leaderboard')}
 *   >
 *     <YourButtonContent />
 *   </ProtectedFeatureButton>
 * 
 * Author: Matt Hales
 * Created: January 26, 2026
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useProtectedFeature } from '../../hooks/useProtectedFeature';

// Global flag to prevent dropdown from closing when auth modal is open
let authModalOpenCount = 0;

const ProtectedFeatureButton = ({
  children,
  featureName,
  onAccess,
  onAuthRequired,
  className = '',
  showLockBadge = true,
  ...props
}) => {
  const { isAuthenticated, authenticate, isAuthenticating, authError } = useProtectedFeature();
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If already authenticated, just call the onAccess callback
    if (isAuthenticated) {
      if (onAccess) onAccess();
      return;
    }
    
    // Not authenticated - show modal or trigger auth immediately
    if (onAuthRequired) {
      // Custom auth required handler
      onAuthRequired();
      return;
    }
    
    // Default: Show auth modal (rendered via Portal, always perfectly centered)
    // Set global flag IMMEDIATELY (before state update) to prevent dropdown from closing
    window.__authModalOpen = true;
    console.log('🔐 Setting global flag immediately before modal opens');
    setShowAuthModal(true);
  };
  
  const handleAuthenticate = async () => {
    console.log('🔐 handleAuthenticate called for:', featureName);
    const success = await authenticate();
    console.log('🔐 Authentication result:', success);
    
    if (success) {
      setShowAuthModal(false);
      // Call onAccess after successful auth
      if (onAccess) {
        console.log('✅ Calling onAccess for:', featureName);
        onAccess();
      }
    }
  };
  
  // Track auth modal open/close to prevent parent dropdown from closing
  useEffect(() => {
    if (showAuthModal) {
      authModalOpenCount++;
      // Set global flag that can be checked by Header
      window.__authModalOpen = true;
      console.log('🔐 Auth modal opened, set global flag. Count:', authModalOpenCount);
    } else {
      authModalOpenCount--;
      if (authModalOpenCount <= 0) {
        authModalOpenCount = 0;
        window.__authModalOpen = false;
        console.log('🔐 Auth modal closed, cleared global flag');
      }
    }
    
    return () => {
      if (showAuthModal) {
        authModalOpenCount--;
        if (authModalOpenCount <= 0) {
          authModalOpenCount = 0;
          window.__authModalOpen = false;
        }
      }
    };
  }, [showAuthModal]);
  
  return (
    <>
      {/* Button Wrapper */}
      <div 
        className={`relative ${className}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <button
          onClick={handleClick}
          className="w-full h-full"
          disabled={isAuthenticating}
        >
          {children}
        </button>
        
        {/* Lock Badge (only when not authenticated) */}
        {showLockBadge && !isAuthenticated && (
          <div className="absolute top-1 right-1 bg-[var(--pbfs-navy)] border border-white/20 rounded-full px-1.5 py-0.5 shadow-lg pointer-events-none">
            <span className="text-[10px] text-white/80">🔒</span>
          </div>
        )}
        
        {/* Authenticating Spinner Overlay */}
        {isAuthenticating && (
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm rounded-lg flex items-center justify-center pointer-events-none">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        )}
      </div>
      
      {/* Authentication Modal - Rendered via Portal to ensure proper centering */}
      {showAuthModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" data-auth-modal="true">
          <div className="bg-[var(--pbfs-navy)] border-2 border-[var(--pbfs-green)]/30 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scaleIn">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--pbfs-green)] to-[#008f35] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">🔐</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Sign In Required
              </h3>
              <p className="text-white/70 text-sm">
                {featureName || 'This feature'} requires authentication with your Pye-Barker Fire & Safety account.
              </p>
            </div>
            
            {/* Error Message */}
            {authError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">❌ {authError}</p>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAuthenticate();
                }}
                disabled={isAuthenticating}
                className="w-full bg-gradient-to-r from-[var(--pbfs-green)] to-[#008f35] text-white font-bold py-3 px-6 rounded-lg hover:from-[#0a9d42] hover:to-[#007a2d] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
              >
                {isAuthenticating ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Connecting...</span>
                  </div>
                ) : (
                  <>💼 Continue with Microsoft</>
                )}
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('❌ User clicked Cancel');
                  setShowAuthModal(false);
                }}
                disabled={isAuthenticating}
                className="w-full bg-white/5 text-white/70 hover:text-white font-medium py-2.5 px-4 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Cancel
              </button>
            </div>
            
            {/* Security Note */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-white/40 text-xs text-center">
                🛡️ Your data is protected by Microsoft Entra ID
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProtectedFeatureButton;
